import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  Skeleton,
} from '@mui/material';
import ModalMAIQ from 'app/components/ModalMAIQ';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import ConfirmationModal from 'app/components/ConfirmationModal';
import SingleDatePickerMAIQ from 'app/components/SingleDatePickerMAIQ';

interface DealerSpecialsDetailsModalProps {
  open: boolean;
  onClose: () => void;
  data: any;
  onSave: (payload: any) => void;
  onDelete: () => void;
}

const applicableModelOptions = ['CPO', 'New', 'Used', 'All'];

const DealerSpecialsDetailsModal: React.FC<DealerSpecialsDetailsModalProps> = ({
  open,
  onClose,
  data,
  onSave,
  onDelete,
}) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [form, setForm] = React.useState({
    special_headline: '',
    special_description: '',
    start_date: '',
    end_date: '',
    applicable_model: [] as string[],
    disclaimer_text: '',
  });

  React.useEffect(() => {
    if (data) {
      setForm({
        special_headline: data.special_headline || '',
        special_description: data.special_description || '',
        start_date: data.start_date ? data.start_date.split('T')[0] : '',
        end_date: data.end_date ? data.end_date.split('T')[0] : '',
        applicable_model: Array.isArray(data.applicable_model) 
          ? data.applicable_model 
          : data.applicable_model 
            ? [data.applicable_model] 
            : [],
        disclaimer_text: data.disclaimer_text || '',
      });
    }
  }, [data]);

  const handleChange = (field: string, value: any) => {
    setForm(prev => ({
      ...prev,
      [field]: field === 'applicable_model'
        ? Array.isArray(value)
          ? value
          : [value]
        : value,
    }));
  };

  const handleSaveClick = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
    setIsEditing(false);
  };

  if (!data) return null;

  return (
    <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton={true}>
      <Paper
        elevation={0}
        sx={{ width: '600px', maxWidth: '90vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <Box
          sx={{ p: 2, backgroundColor: theme.palette.secondary.main, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6" fontWeight={600}>
            Dealer Special Details
          </Typography>
          <Box>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                sx={{ color: 'white' }}
                onClick={() => setIsEditing(true)}
                disabled={isEditing}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" sx={{ color: 'white', ml: 1 }} onClick={() => setOpenDeleteConfirm(true)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton size="small" sx={{ color: 'white' }} onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, overflowY: 'auto' }}>
          {/* Special Headline */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Special Headline
            </Typography>
            {isEditing ? (
              <TextField
                fullWidth
                value={form.special_headline}
                onChange={e => handleChange('special_headline', e.target.value)}
                size="small"
              />
            ) : (
              <Typography variant="body2">{data.special_headline}</Typography>
            )}
          </Box>

          {/* Special Description */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Special Description
            </Typography>
            {isEditing ? (
              <TextField
                fullWidth
                multiline
                rows={3}
                value={form.special_description}
                onChange={e => handleChange('special_description', e.target.value)}
                size="small"
              />
            ) : (
              <Typography variant="body2">{data.special_description}</Typography>
            )}
          </Box>

          {/* Start Date */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Start Date
            </Typography>
            {isEditing ? (
              <SingleDatePickerMAIQ
                value={form.start_date}
                onChange={(date) => handleChange('start_date', date || '')}
                placeholder="Select start date"
              />
            ) : (
              <Typography variant="body2">
                {data.start_date ? new Date(data.start_date).toLocaleDateString() : 'No start date'}
              </Typography>
            )}
          </Box>

          {/* End Date */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              End Date
            </Typography>
            {isEditing ? (
              <SingleDatePickerMAIQ
                value={form.end_date}
                onChange={(date) => handleChange('end_date', date || '')}
                placeholder="Select end date"
                minDate={form.start_date ? new Date(form.start_date) : undefined}
              />
            ) : (
              <Typography variant="body2">
                {data.end_date ? new Date(data.end_date).toLocaleDateString() : 'No end date'}
              </Typography>
            )}
          </Box>

          {/* Applicable Model */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Applicable Model
            </Typography>
            {isEditing ? (
              <FormControl fullWidth>
                {/* <InputLabel>Applicable Model</InputLabel> */}
                <Select
                  multiple
                  value={form.applicable_model}
                  onChange={e => handleChange('applicable_model', e.target.value)}
                  label="Applicable Model"
                  renderValue={selected => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected || []).map((value: string) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {applicableModelOptions.map(option => (
                    <MenuItem key={option} value={option}>
                      <Checkbox
                        checked={form.applicable_model.indexOf(option) > -1}
                      />
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {Array.isArray(data.applicable_model) 
                  ? data.applicable_model.map((value: string) => (
                      <Chip key={value} label={value} size="small" />
                    ))
                  : data.applicable_model ? (
                      <Chip label={data.applicable_model} size="small" />
                    ) : (
                      <Typography variant="body2">No applicable models selected</Typography>
                    )
                }
              </Box>
            )}
          </Box>

          {/* Disclaimer Text */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Disclaimer Text
            </Typography>
            {isEditing ? (
              <TextField
                fullWidth
                multiline
                rows={2}
                value={form.disclaimer_text}
                onChange={e => handleChange('disclaimer_text', e.target.value)}
                size="small"
              />
            ) : (
              <Typography variant="body2">{data.disclaimer_text || 'No disclaimer'}</Typography>
            )}
          </Box>
        </Box>
        <Divider />

        {/* Footer action bar */}
        {isEditing && (
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: 'white',
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              zIndex: 9,
              flexShrink: 0,
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setIsEditing(false)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Cancel
            </Button>
            {saving ? (
              <Skeleton
                variant="rectangular"
                width={140}
                height={36}
                sx={{
                  borderRadius: 1,
                  bgcolor: 'rgba(15, 40, 76, 0.1)',
                }}
              />
            ) : (
              <Button
                variant="contained"
                onClick={handleSaveClick}
                sx={{ textTransform: 'none', fontWeight: 600, bgcolor: theme.palette.secondary.main, color: 'white' }}
              >
                Save Changes
              </Button>
            )}
          </Box>
        )}
      </Paper>
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        onConfirm={async () => {
          setIsDeleting(true);
          try {
            await onDelete();
            setOpenDeleteConfirm(false);
          } finally {
            setIsDeleting(false);
          }
        }}
        message="Are you sure you want to delete this dealer special?"
        loading={isDeleting}
      />
    </ModalMAIQ>
  );
};

export default DealerSpecialsDetailsModal; 